import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { JanusService, StateService } from '@shared/services';
import { NotificationService } from '@shared/services/notification/notification.service';
import { delay, distinctUntilChanged, map, skip, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { RecordingService } from '../../../services/recording.service';
import { TokenService } from '@shared/services/token.service';
import { CustomizationService } from '../../../services/customization.service';
import { RecordingState } from '../../../enums/recording-state';
import { SoundService } from '@shared/services/sound.service';
import { RealtimeDatabaseSubscriptionsService } from '../../../services/rtdb-subscriptions.service';

@Component({
  selector: 'thevatra-recording-control',
  templateUrl: './recording-control.component.html',
  styleUrls: ['./recording-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordingControlComponent implements OnInit, OnDestroy {
  @Input('active') set active(value: boolean) {
    if ((this.recordingState$.getValue() === RecordingState.recording) !== value) {
      this.toggleRecording();
    }
  }
  public recordingState$: BehaviorSubject<RecordingState> = new BehaviorSubject<RecordingState>(RecordingState.stop);
  public loading$ = new BehaviorSubject<boolean>(false);
  private readonly destroyed$ = new Subject<boolean>();
  private roomName: string;

  constructor(
    public readonly stateService: StateService,
    private readonly recordingService: RecordingService,
    private readonly notificationService: NotificationService,
    private readonly janusService: JanusService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly tokenService: TokenService,
    private readonly customizationService: CustomizationService,
    private readonly rtdbSubscriptionsService: RealtimeDatabaseSubscriptionsService,
    private soundService: SoundService,
  ) {
  }

  ngOnInit(): void {
    this.setRoomName();
    // sync this.isRecording$ with server value
    this.rtdbSubscriptionsService.recordingSubscription$
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        map(x => x || RecordingState.stop), // can be null in RTDB, means stopped
      )
      .subscribe(this.recordingState$);

    this.recordingState$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      skip(1), // skip initial value
    ).subscribe((recordingState) => {
      this.changeDetectorRef.detectChanges();
      if (recordingState === RecordingState.recording) {
        this.loading$.next(false);
        this.showMessageRecordingHasBeenStarted();
        this.soundService.playRecordingStarted();
      } else if (recordingState === RecordingState.stop) {
        this.loading$.next(false);
        this.showMessageRecordingHasBeenStopped();
        this.soundService.playRecordingStopped();
      }
    });
  }

  public toggleRecording(): void {
    this.loading$.next(true);
    console.log('[cc] toggle rec. ');
    // this.recordingService.getList(roomName).pipe(takeUntil(this.destroyed$)).subscribe((list) => {
    //   console.log('[rec] list: ', list);
    // });
    if (this.recordingState$.getValue() === RecordingState.pending) { // don't allow fast clicks before prev ended
      return;
    }
    if (this.recordingState$.getValue() === RecordingState.stop) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  private startRecording(): void {
    if (this.stateService.isRecordingBot) {
      throw new Error('AG: startRecording API cannot be used by recording bot');
    }
    // this.recordingService.start(this.roomName, 'https://staging.vatra.live/test2')
    this.recordingService.start(this.roomName, window.location.origin)
      .subscribe(
        (list) => {
          console.log('[rec] start: ', list);
          this.showMessageRecordingInProgress();
        },
        () => this.notificationService.showError('Problem starting recording.')
      );
  }

  private stopRecording(): void {
    if (this.stateService.isRecordingBot) {
      throw new Error('AG: stopRecording API cannot be used by recording bot');
    }
    this.changeDetectorRef.detectChanges();
    this.recordingService.stop(this.roomName)
      .subscribe((list) => {
        console.log('[rec] stop: ', list);
      },
      () => this.notificationService.showError('Problem stopping recording.')
      );
  }

  private setRoomName(): void {
    this.roomName = this.stateService.roomName as string;
  }

  private showMessageRecordingHasBeenStopped(): void {
    this.notificationService.showInfo('Recording has been stopped.');
  }

  private showMessageRecordingHasBeenStarted(): void {
    this.notificationService.showInfo('Recording has started.');
  }

  private showMessageRecordingInProgress(): void {
    this.notificationService.showInfo('Recording will start in few seconds.');
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
